import React, { FC } from "react";
import classnames from "classnames";

import { GeneralProps } from "components/shared/types";

import styles from "./grid-col.module.scss";

type GridSize = number;

type GridParams = GridSize | { size: number; order?: number };

type ViewportName = "xs" | "sm" | "md" | "lg" | "xl" | "xxl";

type Props = {
  size?: GridParams;
  xs?: GridParams;
  sm?: GridParams;
  md?: GridParams;
  lg?: GridParams;
  xl?: GridParams;
  xxl?: GridParams;
  alignItems?: "flex-start" | "center" | "flex-end" | "stretch" | "baseline";
  justifyContent?:
    | "flex-start"
    | "center"
    | "flex-end"
    | "space-between"
    | "space-around"
    | "space-evenly";
} & GeneralProps;

export const GridCol: FC<React.PropsWithChildren<Props>> = (props) => {
  const {
    size,
    xs,
    sm,
    md,
    lg,
    xl,
    xxl,
    alignItems,
    justifyContent,
    dataQa,
    children,
  } = props;

  const generateColumnClassname = (
    gridParams: GridParams,
    viewportName?: ViewportName,
  ) => {
    const columnClassName: string = styles["scss-grid-columns-classname-base"];

    const columnSize =
      typeof gridParams === "object" && gridParams.size
        ? gridParams.size
        : gridParams;

    if (columnSize === undefined) return "";

    if (viewportName) {
      return `${columnClassName}-${viewportName}-${columnSize}`;
    } else {
      return `${columnClassName}-${columnSize}`;
    }
  };

  const generateOrderClassname = (
    gridParams: GridParams,
    viewportName?: ViewportName,
  ) => {
    if (typeof gridParams === "object" && gridParams.order) {
      const orderClassNameBase = styles["scss-grid-order-classname-base"];

      if (viewportName) {
        return `${orderClassNameBase}-${viewportName}-${gridParams.order}`;
      } else {
        return `${orderClassNameBase}-${gridParams.order}`;
      }
    }

    return "";
  };

  const getClassName = (
    gridParams?: GridParams,
    viewportName?: ViewportName,
  ) => {
    if (!gridParams) return;

    const columnClassName = generateColumnClassname(gridParams, viewportName);
    const orderClassName = generateOrderClassname(gridParams, viewportName);

    return classnames(
      styles[columnClassName] || "",
      styles[orderClassName] || "",
    );
  };

  const className = classnames({
    [styles.root]: true,
    // We have ho viewport prefix for XS size,
    // so we generate just column-12 classnames, instead of column-xs-12, and so on
    // @ts-ignore
    [getClassName(xs)]: xs !== undefined,
    // @ts-ignore
    [getClassName(size)]: size !== undefined,
    // @ts-ignore
    [getClassName(sm, "sm")]: sm !== undefined,
    // @ts-ignore
    [getClassName(md, "md")]: md !== undefined,
    // @ts-ignore
    [getClassName(lg, "lg")]: lg !== undefined,
    // @ts-ignore
    [getClassName(xl, "xl")]: xl !== undefined,
    // @ts-ignore
    [getClassName(xxl, "xxl")]: xxl !== undefined,
    [styles[`align-items-${alignItems}`]]: Boolean(alignItems),
    [styles[`justify-content-${justifyContent}`]]: Boolean(justifyContent),
  });

  const contentClassname =
    justifyContent !== undefined || alignItems !== undefined
      ? styles.alignedContent
      : styles.content;

  return (
    <div className={className} {...dataQa}>
      <div className={contentClassname}>{children}</div>
    </div>
  );
};
